import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";

import PreviewLinkCard from "../previewLinkCard";
import {
	getCarouselIndicator,
	LinkPreview,
	normalizeLinkPreviews,
} from "./linkPreviewModel";
import {
	hasLinkPreviewMetadata,
	hydrateLinkPreviews,
	LegacyPreviewResult,
} from "./linkPreviewResolver";
import styles from "./styles.module.scss";

const LINK_PREVIEW_RESOLVER_URL =
	"https://g7mpxopqn3.execute-api.eu-west-2.amazonaws.com/staging";

interface IPreviewPostLink {
	linkPreviews?: unknown;
	link_previews?: unknown;
}

const resolveLinkPreviewMetadata = async (
	urls: string[],
): Promise<LegacyPreviewResult[]> => {
	const response = await axios.post(LINK_PREVIEW_RESOLVER_URL, {
		body: { urls },
	});
	const body =
		typeof response?.data?.body === "string"
			? JSON.parse(response.data.body)
			: response?.data?.body;

	return !body?.error && Array.isArray(body?.message) ? body.message : [];
};

const PreviewPostLink: React.FunctionComponent<IPreviewPostLink> = (source) => {
	const trackRef = useRef<HTMLDivElement | null>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const normalizedPreviews = useMemo(
		() => normalizeLinkPreviews(source),
		[source.linkPreviews, source.link_previews],
	);
	const initialPreviews = useMemo(
		() => normalizedPreviews.filter(hasLinkPreviewMetadata),
		[normalizedPreviews],
	);
	const [hydrated, setHydrated] = useState<{
		source: LinkPreview[];
		previews: LinkPreview[];
	}>(() => ({
		source: normalizedPreviews,
		previews: initialPreviews,
	}));
	const previews =
		hydrated.source === normalizedPreviews
			? hydrated.previews
			: initialPreviews;
	const indicator = getCarouselIndicator(previews.length, activeIndex);

	useEffect(() => {
		let active = true;
		setHydrated({
			source: normalizedPreviews,
			previews: initialPreviews,
		});
		hydrateLinkPreviews(
			normalizedPreviews,
			resolveLinkPreviewMetadata,
		).then((resolved) => {
			if (active) {
				setHydrated({
					source: normalizedPreviews,
					previews: resolved,
				});
			}
		});

		return () => {
			active = false;
		};
	}, [normalizedPreviews, initialPreviews]);

	const setIndexFromScroll = () => {
		const track = trackRef.current;
		if (!track?.clientWidth) return;
		setActiveIndex(
			Math.min(
				Math.max(Math.round(track.scrollLeft / track.clientWidth), 0),
				previews.length - 1,
			),
		);
	};

	const move = (direction: -1 | 1) => {
		const track = trackRef.current;
		if (!track) return;
		track.scrollTo({
			left: track.clientWidth * (activeIndex + direction),
			behavior: "smooth",
		});
	};

	if (previews.length === 0) return null;

	return (
		<div className={styles.carousel}>
			<div
				ref={trackRef}
				className={styles.track}
				onScroll={setIndexFromScroll}
				onKeyDown={(event) => {
					if (event.key === "ArrowLeft") move(-1);
					if (event.key === "ArrowRight") move(1);
				}}
				role="region"
				aria-label="Link previews"
				tabIndex={previews.length > 1 ? 0 : -1}
			>
				{previews.map((preview, index) => (
					<div
						className={styles.slide}
						key={`${preview.url}-${index}`}
					>
						<PreviewLinkCard preview={preview} />
					</div>
				))}
			</div>
			{previews.length > 1 ? (
				<>
					<button
						type="button"
						className={`${styles.arrow} ${styles.previous}`}
						onClick={() => move(-1)}
						disabled={activeIndex === 0}
						aria-label="Previous link preview"
					>
						‹
					</button>
					<button
						type="button"
						className={`${styles.arrow} ${styles.next}`}
						onClick={() => move(1)}
						disabled={activeIndex === previews.length - 1}
						aria-label="Next link preview"
					>
						›
					</button>
				</>
			) : null}
			{indicator.kind === "dots" ? (
				<div className={styles.indicators} aria-hidden="true">
					{Array.from({ length: indicator.count }, (_, index) => (
						<span
							key={`link-preview-dot-${index}`}
							className={
								index === indicator.activeIndex
									? styles.activeDot
									: styles.dot
							}
						/>
					))}
				</div>
			) : indicator.kind === "counter" ? (
				<div className={styles.counter}>{indicator.label}</div>
			) : null}
		</div>
	);
};

export default PreviewPostLink;
